import { Button, Drawer } from '@mantine/core'
import { IconInfoCircle, IconUser } from '@tabler/icons-react'
import { TONE_PILL, type CareJourney, type JourneyKpi, type JourneyReason } from './journeys'

const KPI_TITLES: Record<JourneyKpi, string> = {
  active: 'Active Care Journeys',
  'awaiting-results': 'Awaiting Results',
  'results-need-followup': 'Results Need Follow-up',
  completed: 'Completed Care Journeys',
}

// The drawer's stated definition of each metric, so a number on a card is never
// ambiguous. The Active wording is the ticket's "recommended drawer definition".
const KPI_DEFINITIONS: Record<JourneyKpi, string> = {
  active: 'Patients with at least one open, overdue, or future care action.',
  'awaiting-results':
    'Orders sent to a lab, imaging center, or other organization where the expected result has not been received.',
  'results-need-followup':
    'Results received where a practitioner follow-up or review is required but not completed.',
  completed:
    'Patients whose every visit in scope has nothing left outstanding — the exact complement of Active Care Journeys.',
}

/** Short status pill on the right of each example row. */
function pillLabel(reason: JourneyReason): string {
  if (reason.state === 'overdue') return 'Overdue'
  if (reason.state === 'future') return 'Future'
  if (reason.kpi === 'awaiting-results') return 'Awaiting result'
  if (reason.state === 'completed') return 'Completed'
  return 'Open'
}

/** The single reason that explains why this patient is in this KPI. */
function reasonFor(journey: CareJourney, kpi: JourneyKpi): JourneyReason | undefined {
  const matching = journey.reasons.filter((reason) => reason.kpi === kpi)
  // Prefer the most urgent explanation when a patient qualifies several ways.
  return (
    matching.find((reason) => reason.state === 'overdue') ??
    matching[0] ??
    // The Active card is a union of every other reason, so fall back to any.
    (kpi === 'active' ? journey.reasons[0] : undefined)
  )
}

const EXAMPLE_LIMIT = 5

export const KpiDetailDrawer = ({
  kpi,
  journeys,
  onClose,
  onViewAll,
}: {
  kpi: JourneyKpi | null
  /** Already narrowed to the patients counted under `kpi`. */
  journeys: CareJourney[]
  onClose: () => void
  onViewAll: () => void
}) => (
  <Drawer
    opened={kpi !== null}
    onClose={onClose}
    position="right"
    size={440}
    title={<span className="text-xl font-semibold">{kpi ? KPI_TITLES[kpi] : ''}</span>}
  >
    {kpi ? (
      <div className="flex flex-col gap-5">
        {/* No negative top margin: it pulled this line up into the drawer's
            header, which clips its overflow, and the count rendered sliced in
            half. The body's own padding is the spacing that belongs here. */}
        <p className="text-sm text-gray-500">
          {journeys.length} {journeys.length === 1 ? 'patient' : 'patients'}
        </p>

        <p className="border-t border-gray-200 pt-4 text-sm text-gray-700">
          {KPI_DEFINITIONS[kpi]}
        </p>

        {journeys.length ? (
          <div>
            <h3 className="mb-3 text-base font-semibold text-gray-900">Examples</h3>
            <ul className="flex flex-col gap-3">
              {journeys.slice(0, EXAMPLE_LIMIT).map((journey) => {
                const reason = reasonFor(journey, kpi)
                return (
                  <li
                    key={journey.patientId}
                    className="flex items-start gap-3 rounded-lg border border-gray-200 p-3"
                  >
                    <IconUser size={18} className="mt-0.5 shrink-0 text-gray-400" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {journey.patientName}
                      </p>
                      {reason ? (
                        <p className="text-sm text-gray-500">
                          {reason.text}
                          {reason.days !== null && reason.days > 0
                            ? ` · ${reason.days} ${reason.days === 1 ? 'day' : 'days'}`
                            : ''}
                        </p>
                      ) : null}
                    </div>
                    {reason ? (
                      <span
                        className={`shrink-0 rounded px-2 py-0.5 text-xs ${TONE_PILL[reason.tone]}`}
                      >
                        {pillLabel(reason)}
                      </span>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}

        <div className="flex items-start gap-2 rounded-lg bg-gray-50 p-3">
          <IconInfoCircle size={18} className="mt-0.5 shrink-0 text-gray-400" />
          {/* Reversed for the Completed card, where a settled visit is exactly
              what IS counted — the shared note read as a contradiction there. */}
          <p className="text-sm text-gray-600">
            {kpi === 'completed'
              ? 'A patient appears here only once every visit in scope is settled.'
              : 'Completed visits with no remaining action are not counted.'}
          </p>
        </div>

        {journeys.length ? (
          <Button fullWidth size="md" onClick={onViewAll}>
            View all {journeys.length} {journeys.length === 1 ? 'patient' : 'patients'}
          </Button>
        ) : null}
      </div>
    ) : null}
  </Drawer>
)
